import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

type NameStepStateStore = {
  isInNameStep: boolean;
  setInNameStep: () => void;
  clearNameStep: () => void;
};

const useNameStepStateStore = create<NameStepStateStore>()(
  persist(
    (set) => ({
      isInNameStep: false,
      setInNameStep: () => {
        set({ isInNameStep: true });
      },
      clearNameStep: () => {
        set({ isInNameStep: false });
      },
    }),
    { storage: createJSONStorage(() => sessionStorage), name: "twi.nameStepState" },
  ),
);

/**
 * React hook for managing name step state.
 * Provides a reactive interface to the name step state stored in sessionStorage.
 *
 * Uses Zustand with persist middleware for automatic sessionStorage synchronization
 * and cross-tab updates.
 */
export function useNameStepState() {
  const isInNameStep = useNameStepStateStore((state) => state.isInNameStep);
  const setInNameStep = useNameStepStateStore((state) => state.setInNameStep);
  const clearNameStep = useNameStepStateStore((state) => state.clearNameStep);

  return {
    isInNameStep,
    setInNameStep,
    clearNameStep,
  };
}

/**
 * Pure function to check if user is in name step.
 * Useful for non-hook contexts.
 */
export function checkIsInNameStep(): boolean {
  const state = useNameStepStateStore.getState();
  return state.isInNameStep;
}

export function subscribeToNameStepState(listener: (isInNameStep: boolean) => void) {
  return useNameStepStateStore.subscribe((state, previousState) => {
    if (state.isInNameStep !== previousState.isInNameStep) {
      listener(state.isInNameStep);
    }
  });
}
